import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOptionalOverlayBackDismiss } from '../../shared/context/OverlayBackDismissContext.js';
import { BottomSheet } from '../../shared/ui/BottomSheet.js';
import { MoreMenuPanel } from './MorePage.js';

interface MoreDrawerProps {
  readonly open: boolean;
  readonly compact: boolean;
  readonly onClose: () => void;
}

/**
 * More menu sheet — wrap-in-place on shared BottomSheet (trap/restore/Escape/backdrop).
 * OverlayBackDismiss stays at this call site (ban naive wrap that strips it).
 */
export function MoreDrawer({ open, compact, onClose }: MoreDrawerProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('more.title')}
      titleId="customer-more-drawer-title"
      closeLabel={t('shop.checkoutModeSheetCancel')}
      testId="customer-more-drawer"
      className="max-w-lg bg-[var(--color-surface-muted)] sm:max-h-[92vh] sm:rounded-2xl sm:p-5"
      containerClassName="lg:items-center"
    >
      <MoreMenuPanel compact={compact} />
    </BottomSheet>
  );
}
